// ============================================
// FILE: src/components/delivery/DeliveryPincodeHint.jsx
// ============================================
//
// One line under a pincode field saying whether we deliver there. Used by the
// address forms: it informs, it never blocks saving.

import React, { useState, useEffect } from 'react';
import { Truck, AlertCircle } from 'lucide-react';
import apiService from '../../apiService';
import { isValidPincode, formatEta, describeShipping } from '../../utils/deliveryHelpers';

// Looks a pincode up once it is complete, debounced so typing doesn't fire a
// request per keystroke. Returns null until there is an answer.
export const useDeliveryCheck = (pincode) => {
  const [delivery, setDelivery] = useState(null);

  useEffect(() => {
    setDelivery(null);
    if (!isValidPincode(pincode)) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      const response = await apiService.checkDeliveryPincode(pincode);
      if (!cancelled && response.success && response.data?.delivery) {
        setDelivery(response.data.delivery);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pincode]);

  return delivery;
};

const DeliveryPincodeHint = ({ pincode }) => {
  const delivery = useDeliveryCheck(pincode);
  if (!delivery) return null;

  if (delivery.serviceable === false) {
    return (
      <p className="mt-1.5 flex items-start gap-1.5" style={{ fontSize: 12, color: 'var(--fx-pink)' }}>
        <AlertCircle size={14} style={{ marginTop: 1, flexShrink: 0 }} />
        We don't deliver to {String(pincode).trim()} yet. You can save this address, but orders can't be placed to it.
      </p>
    );
  }

  const details = [formatEta(delivery), describeShipping(delivery)].filter(Boolean).join(' · ');

  return (
    <p className="mt-1.5 flex items-start gap-1.5" style={{ fontSize: 12, color: 'var(--fx-green)' }}>
      <Truck size={14} style={{ marginTop: 1, flexShrink: 0 }} />
      We deliver here{details ? ` · ${details}` : ''}
    </p>
  );
};

export default DeliveryPincodeHint;
